// Pure helpers for the ci-pipeline hooks.
//
// Nothing in this module touches the DOM, canvas, gsap, or any global state.
// Every export is a total, deterministic, side-effect-free function of its
// arguments (callers supply `Date.now()` where needed).
// Property-tested in test/ci-pipeline-helpers.test.ts.

import escapeHtml from "escape-html";
import {
  extent,
  max as d3max,
  min as d3min,
  cumsum,
  bisectLeft,
} from "d3-array";
import { line as d3line } from "d3-shape";
import { timeFormat } from "d3-time-format";
import {
  relativeTime,
  formatDurationMs as doxFormatDurationMs,
  formatDurationSec,
  formatBytes,
  knownNixSystemSuffixes,
} from "../../../../.dox/index";
import {
  JobStatus,
  FlakeOutputType,
  StatusColorKey,
  illuminateStatusColor,
  jobStatusLabel,
  jobStatusColorKey,
  jobStatusColor,
} from "../../../../.dox/ci";

// -- Shared types ------------------------------------------------------------

export type { JobStatus, FlakeOutputType, StatusColorKey };

export interface DagNode {
  readonly id: string;
  readonly label: string;
  readonly attrPath: string | null;
  readonly status: JobStatus;
  readonly outputType: FlakeOutputType | null;
  readonly nixSystem: string | null;
  readonly cacheHit: boolean;
  readonly isUserOutput: boolean;
  readonly isFlaky: boolean;
  readonly durationMs: number | null;
  readonly estimatedMs?: number | null;
  readonly buildStartedAt?: number | null;
}

export interface DagEdge {
  readonly source: string;
  readonly target: string;
}

export interface GraphData {
  readonly nodes: ReadonlyArray<DagNode>;
  readonly edges: ReadonlyArray<DagEdge>;
}

// -- Numeric helpers ----------------------------------------------------------

/** Linear interpolation. lerp(a, b, 0) = a, lerp(a, b, 1) = b. */
export const lerp = (a: number, b: number, t: number): number =>
  t === 0 ? a : t === 1 ? b : a + (b - a) * t;

/**
 * Pad or truncate an array to a target length.
 * - Shorter input is right-padded with the last value (or null if empty).
 * - Longer input is truncated from the right.
 * - Equal-length input is returned unchanged.
 *
 * Invariants:
 *   output.length === len
 *   when arr.length >= len, output is the prefix of arr of size len
 */
export const conformLength = <T>(
  arr: ReadonlyArray<T | null>,
  len: number,
): Array<T | null> => {
  if (arr.length === len) return arr.slice();
  if (arr.length === 0) return new Array<T | null>(len).fill(null);
  if (arr.length < len) {
    const last = arr[arr.length - 1] ?? null;
    const pad = new Array<T | null>(len - arr.length).fill(last);
    return [...arr, ...pad];
  }
  return arr.slice(0, len);
};

/**
 * Linearly resample a numeric series to a new length, stretching the shape
 * across the new indices. Used to morph sparklines whose old/new point counts
 * differ without producing flat right-edge tails (unlike `conformLength`).
 *
 * Null values propagate: if either neighbour sample is null, the result is
 * that null (so gaps survive resampling rather than being silently bridged).
 *
 * Invariants:
 *   output.length === len
 *   output[0] === arr[0], output[len-1] === arr[arr.length-1] (when arr.length >= 2)
 */
export const resampleToLength = (
  arr: ReadonlyArray<number | null>,
  len: number,
): Array<number | null> => {
  if (arr.length === len) return arr.slice();
  if (arr.length === 0) return new Array<number | null>(len).fill(null);
  if (arr.length === 1 || len === 1)
    return new Array<number | null>(len).fill(arr[0] ?? null);
  const out: Array<number | null> = new Array<number | null>(len);
  const scale = (arr.length - 1) / (len - 1);
  for (let i = 0; i < len; i++) {
    // Snap endpoints to avoid floating-point drift that would otherwise lerp
    // by a near-but-not-exactly-1 factor and lose ULPs at the tail.
    if (i === 0) {
      out[i] = arr[0] ?? null;
      continue;
    }
    if (i === len - 1) {
      out[i] = arr[arr.length - 1] ?? null;
      continue;
    }
    const srcPos = i * scale;
    const lo = Math.floor(srcPos);
    const hi = Math.min(arr.length - 1, Math.ceil(srcPos));
    const t = srcPos - lo;
    const vLo = arr[lo];
    const vHi = arr[hi];
    if (vLo == null || vHi == null) out[i] = null;
    else out[i] = vLo + (vHi - vLo) * t;
  }
  return out;
};

// -- SVG path builders --------------------------------------------------------

export interface SparklineMargin {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

/**
 * Build an SVG `d` path string for a sparkline line chart.
 * Auto-scales y to fill the chart area. Null values create real gaps —
 * d3's `.defined()` predicate emits a fresh M command for each contiguous
 * run of non-null samples instead of bridging them with a straight line.
 * Returns "" if fewer than 2 valid points.
 */
export const sparklineLinePath = (
  points: ReadonlyArray<number | null>,
  w: number,
  h: number,
  margin: SparklineMargin,
): string => {
  const valid = points.filter((v): v is number => v != null && !isNaN(v));
  if (valid.length < 2) return "";

  const innerW = w - margin.left - margin.right;
  const innerH = h - margin.top - margin.bottom;

  // d3.extent avoids `Math.min(...big)` which stack-blows around 125k args.
  const [yMin = 0, yMax = 1] = extent(valid);
  const yRange = yMax - yMin || 1;

  // X maps ORIGINAL-array index to chart width, so a `[5, null, 7]` series
  // shows the gap in the correct place rather than collapsing the two
  // valid samples into adjacent points. The previous implementation
  // indexed by valid-order, which silently bridged null-gap pairs.
  const xStep = innerW / Math.max(points.length - 1, 1);
  const xOf = (i: number): number => margin.left + i * xStep;
  const yOf = (val: number): number =>
    margin.top + innerH - ((val - yMin) / yRange) * innerH;

  const pathGen = d3line<{ v: number | null; i: number }>()
    .x((d) => xOf(d.i))
    .y((d) => yOf(d.v ?? 0))
    .defined((d) => d.v != null && !isNaN(d.v));
  return pathGen(points.map((v, i) => ({ v, i }))) ?? "";
};

// -- Morph helpers ------------------------------------------------------------

/**
 * Compute a fixed y-axis range that spans all valid values in both datasets.
 * Used as a constant scale during sparkline morph so that point movement is
 * visible (instead of auto-scaling each frame, which normalises shape changes
 * away). Falls back to {0, 1} when both arrays are empty or all-null.
 */
export const morphYRange = (
  oldPoints: ReadonlyArray<number | null>,
  newPoints: ReadonlyArray<number | null>,
): { min: number; max: number } => {
  const all = [...oldPoints, ...newPoints].filter(
    (v): v is number => v != null && !isNaN(v),
  );
  if (all.length === 0) return { min: 0, max: 1 };
  // d3.min/max iterate rather than spread, so large arrays don't blow the
  // argument stack the way `Math.min(...all)` does past ~125k entries.
  return { min: d3min(all) ?? 0, max: d3max(all) ?? 0 };
};

/**
 * Per-point linear interpolation with null handling.
 *   - value → value: standard lerp
 *   - null  → value: fade from `baseline` to the new value
 *   - value → null:  fade from the old value to `baseline`
 *   - null  → null:  stays null
 *
 * `baseline` is typically the y-axis minimum so appearing/disappearing points
 * grow from / shrink toward the bottom of the chart.
 */
export const interpolatePoints = (
  old: ReadonlyArray<number | null>,
  next: ReadonlyArray<number | null>,
  t: number,
  baseline = 0,
): Array<number | null> =>
  old.map((ov, i): number | null => {
    const nv: number | null = next[i] ?? null;
    if (ov != null && nv != null) return lerp(ov, nv, t);
    if (ov == null && nv == null) return null;
    // Null → value: grow from baseline. Value → null: shrink to baseline.
    if (ov == null) return lerp(baseline, nv!, t);
    return lerp(ov, baseline, t);
  });

// -- KDE helpers --------------------------------------------------------------

/**
 * Max of a numeric array with a positive floor.
 *
 * Why a wrapper: `Math.max(...arr)` blows the V8 call stack at ~125k args.
 * KDE grids are typically <500 points so the immediate hazard is small, but
 * the spread-apply foot-gun has bitten production charts elsewhere and there
 * is no reason to keep paying for it. `d3-array.max` walks the array.
 *
 * The `floor` argument is the original "OR floor" pattern (`Math.max(...d, 1e-9)`)
 * — it protects callers that use the result as a denominator (y-axis scale)
 * from div-by-zero when every density is 0. Defaults to 0 (no floor).
 */
export const safeMax = (
  values: ReadonlyArray<number>,
  floor: number = 0,
): number => Math.max(d3max(values) ?? 0, floor);

/**
 * Index of the median bin for a discrete density distribution.
 *
 * The median is the smallest index `i` such that `sum(densities[0..i]) >=
 * total/2`. This matches the original imperative scan exactly, including
 * the all-zero edge case (returns 0 because `bisectLeft([0..0], 0)` is 0).
 *
 * Uses `cumsum` + `bisectLeft` → O(n) but with one pass and no early-exit
 * branch in the hot loop; clearer intent than the hand-rolled scan that was
 * duplicated across two callsites.
 */
export const kdeMedianIndex = (densities: ReadonlyArray<number>): number => {
  if (densities.length === 0) return 0;
  const cum = cumsum(densities);
  const total = cum[cum.length - 1] ?? 0;
  return bisectLeft(cum, total / 2);
};

// kdeResample implementation lives in ./kde-morph (no .dox dep, testable).
export { kdeResample } from "./kde-morph";

// -- Duration / time formatters ----------------------------------------------

/**
 * Relative-time string tuned for sparkline axis labels.
 * Matches the shape of Elixir's relative_time helper but uses unix-ms input.
 * Pure: callers must supply `now` (typically `Date.now()` at the call site).
 */
export const sparklineTimeAgo = (unixMs: number, now: number): string => {
  const diff = Math.trunc((now - unixMs) / 1000);
  const label = relativeTime(diff);
  if (label !== "long ago") return label;
  const d = new Date(unixMs);
  return `${d.toLocaleString("en", { month: "short" })} ${d.getDate()}`;
};

/** Seconds → compact string. Used for chart axis tick labels.
 *
 * Sub-second values (0 < s < 1) collapse to "Nms" so charts of fast
 * jobs don't degenerate into a row of "0s" tick labels. s == 0 stays
 * "0s" (no-data placeholder) and integer second values are unchanged.
 *
 * Minute-range values keep the seconds component when non-zero — a
 * KDE chart whose domain spans a couple of minutes used to render
 * every tick as the same "2m" because `Math.round(s/60)` squashed
 * the entire 60-179s band to that single label. Emitting "1m 30s",
 * "2m 15s" etc. keeps consecutive ticks distinguishable.
 */
export const formatDuration = (s: number): string => {
  if (s > 0 && s < 1) return `${Math.round(s * 1000)}ms`;
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) {
    let m = Math.floor(s / 60);
    let secs = Math.round(s - m * 60);
    // Carry — 119.7s would round to m=1, secs=60 and print "1m 60s".
    // Promote to the next minute so callers always see a well-formed
    // `[0, 59]` seconds component.
    if (secs === 60) {
      m += 1;
      secs = 0;
    }
    return secs === 0 ? `${m}m` : `${m}m ${secs}s`;
  }
  return `${(s / 3600).toFixed(1)}h`;
};

/** Milliseconds → humanized duration. Generated from helpers.dox. */
export const formatDurationMs = (ms: number): string =>
  doxFormatDurationMs(Math.round(ms));

export { formatDurationSec, formatBytes };

// d3-time-format defaults to the en-US locale (`%b` → "Jan"-"Dec"), which
// matches the hand-rolled English month names the previous implementation
// used.  `%-d` strips the day-of-month padding so "Feb 4" stays as "Feb 4"
// instead of jumping to "Feb 04" — preserves the prior output exactly.
const formatDateFmt = timeFormat("%b %-d %H:%M");

/**
 * Unix ms → "Mon DD HH:MM" style timestamp for scatter plot tooltips.
 * Deterministic for a given input (locale-dependent formatting, but no
 * hidden clock reads — the timestamp is the sole input).
 */
export const formatDate = (unixMs: number): string =>
  formatDateFmt(new Date(unixMs));

// -- ANSI escape parsing (for streaming build log) ---------------------------

export interface AnsiSpan {
  readonly text: string;
  readonly classes: string;
}

/**
 * Map a sequence of SGR (Select Graphic Rendition) codes to a CSS class string.
 * Reset (0) clears all accumulated classes, even if mid-sequence.
 */
export const ansiCodesToClasses = (codes: ReadonlyArray<number>): string => {
  const cls: string[] = [];
  for (const c of codes) {
    if (c === 0) return "";
    if (c === 1) cls.push("ansi-bold");
    if (c === 2) cls.push("ansi-dim");
    if (c === 3) cls.push("ansi-italic");
    if (c === 31) cls.push("ansi-red");
    if (c === 32) cls.push("ansi-green");
    if (c === 33) cls.push("ansi-yellow");
    if (c === 34) cls.push("ansi-blue");
    if (c === 35) cls.push("ansi-magenta");
    if (c === 36) cls.push("ansi-cyan");
    if (c === 37) cls.push("ansi-white");
    if (c === 90) cls.push("ansi-gray");
  }
  return cls.join(" ");
};

/** Parse a string with ANSI escapes into styled text spans.
 *
 * Input is expected to contain raw ESC bytes (`\x1b`, 1 char). Nix's
 * `bar-with-logs` renderer emits raw ESC on builder stderr when its
 * `isTTY()` predicate is satisfied — which the runner ensures via
 * script(1) (real PTY on stderr) and `--setenv=TERM=xterm-256color`
 * inside the systemd-run scope (see runner Build.hs:buildArgv).
 * Drop either of those and nix filters every SGR sequence at the
 * renderer, leaving nothing for this parser to see.
 */
export const parseAnsi = (input: string): ReadonlyArray<AnsiSpan> => {
  const spans: AnsiSpan[] = [];
  const parts = input.split(/\x1b\[([0-9;]*)m/);
  let currentClasses = "";
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      const text = parts[i]!;
      if (text.length > 0) spans.push({ text, classes: currentClasses });
    } else {
      const codes = parts[i]!.split(";").map(Number);
      currentClasses = ansiCodesToClasses(codes);
    }
  }
  return spans;
};

/** Render ANSI spans as an HTML string, escaping each span's text content. */
export const spansToHtml = (spans: ReadonlyArray<AnsiSpan>): string =>
  spans
    .map((s) =>
      s.classes
        ? `<span class="${s.classes}">${escapeHtml(s.text)}</span>`
        : escapeHtml(s.text),
    )
    .join("");

// -- Status → color for the scatter plot dots --------------------------------

// Per-status opacity for scatter dots. cached = success-but-quieter
// (already-cached derivations shouldn't compete visually with fresh
// terminal-success dots in the same chart).
const scatterDotOpacity = (s: JobStatus): number =>
  s === "cached" ? 0.5 : 0.8;

/**
 * Status → Tailwind 400-palette RGBA string for scatter dots.
 *
 * Base colour comes from `jobStatusColor` (helpers.dox, codegen'd) so
 * Elixir and TS sides cannot drift. Only the opacity (above) is local
 * — it's a chart-specific concern, not a status-property.
 */
export const scatterDotColor = (status: JobStatus): string =>
  `rgba(${illuminateStatusColor(jobStatusColor(status))}, ${scatterDotOpacity(status)})`;

// -- Status mappings — generated from helpers.dox via Paradox -------------------

export const statusColorKey = (s: JobStatus): StatusColorKey =>
  jobStatusColorKey(s);

export const statusLabel = (s: JobStatus): string => jobStatusLabel(s);

export const statusDash = (s: JobStatus): string =>
  s === "cached" ? "4 2" : "none";

// -- Tailwind 400 palette for status icons (matches list-view SVG colors) -----

// `cached` is the only status that uses a faded variant — same hue as
// `succeeded`, lower opacity — to communicate "result reused from a
// previous run" without competing visually with fresh successes.
export const iconColor = (s: JobStatus): string =>
  s === "cached"
    ? `rgba(${illuminateStatusColor(jobStatusColor(s))}, 0.6)`
    : `rgb(${illuminateStatusColor(jobStatusColor(s))})`;

// -- Graph algorithms --------------------------------------------------------
// Implementations moved to ./dag-layout (no .dox dep — testable in isolation).
export { buildAdjacency, transitiveChain } from "./dag-layout";

// -- DAG display helpers ------------------------------------------------------

/** Deterministic hue from a string — djb2 hash mapped to [0, 360). */
export const hashHue = (s: string): number => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h & 0x7fffffff) % 360;
};

/** True if the string is a known nix system triple (e.g. "x86_64-linux").
 *  Backed by `knownNixSystemSuffixes` in helpers.dox so the TS and Elixir
 *  attr-path helpers can't drift against each other.
 */
export const isNixSystem = (s: string): boolean =>
  knownNixSystemSuffixes.has(s);

/** Extract the meaningful label from a nix attrPath.
 *
 * Drops empty segments (trailing dots) and trailing system identifiers, then
 * returns the last remaining segment. A single-segment path is returned
 * verbatim even if it's a system name — there's no multi-segment structure to
 * skip past. Returns `fallback` only when nothing meaningful is left. */
export const attrPathLabel = (
  attrPath: string | null,
  fallback: string,
): string => {
  if (!attrPath) return fallback;
  const raw = attrPath.split(".").filter((p) => p !== "");
  if (raw.length === 0) return fallback;
  if (raw.length === 1) return raw[0] ?? fallback;
  const filtered = raw.filter((p) => !isNixSystem(p));
  const last = filtered[filtered.length - 1];
  return last ?? fallback;
};

/** SVG path string for a sparkline (used by legacy SVG-based charts). */
export const sparklinePath = (
  points: ReadonlyArray<number>,
  width: number,
  height: number,
  padding: number = 2,
): string => {
  if (points.length === 0) return "";
  const filtered = points.filter((p) => !isNaN(p));
  if (filtered.length === 0) return "";

  // The trailing 1 / 0 in the original `Math.max(..., 1)` / `Math.min(..., 0)`
  // are a floor / ceiling — preserve that with explicit clamps.
  const max = Math.max(d3max(filtered) ?? 0, 1);
  const min = Math.min(d3min(filtered) ?? 0, 0);
  const range = max - min || 1;
  const step = (width - 2 * padding) / Math.max(filtered.length - 1, 1);
  const h = height - 2 * padding;

  return filtered
    .map((v, i) => {
      const x = padding + i * step;
      const y = padding + h - ((v - min) / range) * h;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
};
